import React from 'react'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body2 } from '@ui/Typography'

import type { DxTradeOrder } from '@model/DxTrade.types'
import { CurrencyType } from '@model/WalletDto'

import { getClosePrice } from '../hooks/useClosePrice'
import { useConversionRate } from '../hooks/useConversionRatesStore'
import { useCurrentPriceForTrade } from '../hooks/useCurrentPricesStore'
import { useHistoryOrderPnl } from '../hooks/useHistoryOrderPnl'
import { usePnLCalculation } from '../hooks/usePnLCalculation'
import type { PnLCalculationParams } from '../hooks/usePnLCalculation'
import { renderPnl } from '../utils/formatters'

type TypographyComponent = React.ComponentType<any>

interface OrderPnLProps {
  order: DxTradeOrder & { instrumentCurrency?: string | null }
  accountCurrency?: CurrencyType
  Typography?: TypographyComponent
  mode?: 'live' | 'close'
  sensitive?: boolean
}

export const OrderPnL: React.FC<OrderPnLProps> = ({
  order,
  accountCurrency,
  Typography = Body2,
  mode = 'live',
  sensitive = false,
}) => {
  const isCloseMode = mode === 'close'
  const closePrice = getClosePrice(order)
  const livePrice = useCurrentPriceForTrade(order.instrument, order.side, 'order')
  const instrumentCurrency = order.instrumentCurrency ?? null
  const { rate } = useConversionRate(instrumentCurrency, accountCurrency)
  const settlementPnl = useHistoryOrderPnl(isCloseMode ? order : undefined)

  const params: PnLCalculationParams = isCloseMode
    ? {
        order,
        exitPrice: closePrice ?? null,
        accountCurrency,
        instrumentCurrency,
        conversionRate: rate,
      }
    : {
        order,
        currentPrice: livePrice ?? null,
        accountCurrency,
        instrumentCurrency,
        conversionRate: rate,
      }

  const value = usePnLCalculation(params)

  const historyValue = settlementPnl ?? null
  const pnlToRender = isCloseMode && historyValue !== null ? historyValue : value
  const currencyToRender = accountCurrency

  const data = renderPnl(pnlToRender, Typography, currencyToRender)

  return <>{sensitive ? <SensitiveData>{data}</SensitiveData> : data}</>
}

export default OrderPnL
